import React, { useMemo } from 'react';
import SortableTable from '../SortableTable';

function TelematicDrivers({ data, dateRange }) {
  // Calculer les statistiques par conducteur
  const driverStats = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Grouper les données par conducteur
    const tripsByDriver = data.reduce((acc, trip) => {
      if (!acc[trip.driver_name]) {
        acc[trip.driver_name] = [];
      }
      acc[trip.driver_name].push(trip);
      return acc;
    }, {});

    // Calculer les statistiques pour chaque conducteur
    return Object.entries(tripsByDriver).map(([driverName, trips]) => {
      // Grouper les trajets par date, en tenant compte des trajets entre minuit et 4h
      const tripsByDate = trips.reduce((acc, trip) => {
        const departureDate = new Date(trip.departure_datetime);

        // Si le trajet commence entre minuit et 4h du matin, on le considère comme faisant partie de la veille
        if (departureDate.getHours() >= 0 && departureDate.getHours() < 4) {
          departureDate.setDate(departureDate.getDate() - 1);
        }

        const date = departureDate.toISOString().split('T')[0];
        if (!acc[date]) {
          acc[date] = [];
        }

        acc[date].push({
          ...trip,
          adjustedDepartureHour: departureDate.getHours() >= 0 && departureDate.getHours() < 4
            ? departureDate.getHours() + 24
            : departureDate.getHours()
        });
        return acc;
      }, {});

      // Pour chaque date, trouver le premier et dernier trajet
      const dailyStats = Object.values(tripsByDate).map(dayTrips => {
        // Trier les trajets par heure de départ
        const sortedTrips = dayTrips.sort((a, b) => {
          const timeA = a.adjustedDepartureHour * 60 + new Date(a.departure_datetime).getMinutes();
          const timeB = b.adjustedDepartureHour * 60 + new Date(b.departure_datetime).getMinutes();
          return timeA - timeB;
        });

        const firstTrip = sortedTrips[0];
        const lastTrip = sortedTrips[sortedTrips.length - 1];

        // Calculer l'heure de départ (premier trajet)
        const departureTime = firstTrip.adjustedDepartureHour * 60 +
          new Date(firstTrip.departure_datetime).getMinutes();

        // Calculer l'heure d'arrivée (dernier trajet)
        const arrivalDate = new Date(lastTrip.arrival_datetime);
        const adjustedArrivalHour = arrivalDate.getHours() >= 0 && arrivalDate.getHours() < 4
          ? arrivalDate.getHours() + 24
          : arrivalDate.getHours();
        const arrivalTime = adjustedArrivalHour * 60 + arrivalDate.getMinutes();

        // Calculer les durées de trajet
        const tripDurations = dayTrips.map(trip => {
          const [hours, minutes, seconds] = trip.trip_time.split(':').map(Number);
          return hours * 60 + minutes + (seconds / 60);
        });

        // Calculer le temps total dans le véhicule
        const totalVehicleTime = tripDurations.reduce((total, duration) => total + duration, 0);

        // Calculer l'amplitude (temps entre premier départ et dernière arrivée)
        const amplitude = arrivalTime - departureTime;

        // Calculer la durée du premier et dernier trajet
        const firstTripDuration = (() => {
          const [hours, minutes, seconds] = firstTrip.trip_time.split(':').map(Number);
          return hours * 60 + minutes + (seconds / 60);
        })();

        const lastTripDuration = (() => {
          const [hours, minutes, seconds] = lastTrip.trip_time.split(':').map(Number);
          return hours * 60 + minutes + (seconds / 60);
        })();

        const daily = dayTrips.map(trip => trip.distance_traveled);
        const totalDistance = daily.reduce((sum, km) => sum + km, 0);

        return {
          departureTime,
          arrivalTime,
          tripDurations,
          tripCount: dayTrips.length,
          totalVehicleTime,
          amplitude,
          firstTripDuration,
          lastTripDuration,
          totalDistance,
          avgDistance: totalDistance / dayTrips.length
        };
      });

      // Calculer les moyennes
      const calculateAverage = (numbers) => numbers.reduce((a, b) => a + b, 0) / numbers.length;

      const avgDepartureTime = calculateAverage(dailyStats.map(stat => stat.departureTime));
      const avgArrivalTime = calculateAverage(dailyStats.map(stat => stat.arrivalTime));
      const avgTripDuration = calculateAverage(dailyStats.flatMap(stat => stat.tripDurations));
      const avgTripsPerDay = calculateAverage(dailyStats.map(stat => stat.tripCount));
      const avgTotalVehicleTime = calculateAverage(dailyStats.map(stat => stat.totalVehicleTime));
      const avgAmplitude = calculateAverage(dailyStats.map(stat => stat.amplitude));
      const avgFirstTripDuration = calculateAverage(dailyStats.map(stat => stat.firstTripDuration));
      const avgLastTripDuration = calculateAverage(dailyStats.map(stat => stat.lastTripDuration));

      // Calculer la distance moyenne par jour et par trajet
      const avgDailyDistance = calculateAverage(dailyStats.map(stat => stat.totalDistance));
      const avgTripDistance = calculateAverage(dailyStats.map(stat => stat.avgDistance));
      const minTripDistance = Math.min(...trips.map(trip => trip.distance_traveled));
      const maxTripDistance = Math.max(...trips.map(trip => trip.distance_traveled));

      // Calculer les min/max pour les durées des premiers et derniers trajets
      const minFirstTripDuration = Math.min(...dailyStats.map(stat => stat.firstTripDuration));
      const maxFirstTripDuration = Math.max(...dailyStats.map(stat => stat.firstTripDuration));
      const minLastTripDuration = Math.min(...dailyStats.map(stat => stat.lastTripDuration));
      const maxLastTripDuration = Math.max(...dailyStats.map(stat => stat.lastTripDuration));

      // Fonction helper pour convertir les minutes en format HH:mm
      const formatTime = (minutes) => {
        if (!minutes && minutes !== 0) return '--:--';
        const hours = Math.floor(minutes / 60);
        const mins = Math.floor(minutes % 60);
        return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
      };

      // Fonction helper pour convertir les minutes en format HH:mm:ss
      const formatDuration = (minutes) => {
        if (!minutes && minutes !== 0) return '--:--:--';
        const hours = Math.floor(minutes / 60);
        const mins = Math.floor(minutes % 60);
        const secs = Math.round((minutes % 1) * 60);
        return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      };

      return {
        driverName,
        totalTrips: trips.length,
        totalDays: Object.keys(tripsByDate).length,
        avgTripsPerDay: avgTripsPerDay.toFixed(1),
        avgDepartureTime: formatTime(avgDepartureTime),
        avgArrivalTime: formatTime(avgArrivalTime),
        avgTripDuration: formatDuration(avgTripDuration),
        avgTotalVehicleTime: formatTime(avgTotalVehicleTime),
        avgAmplitude: formatTime(avgAmplitude),
        avgFirstTripDuration: formatDuration(avgFirstTripDuration),
        minFirstTripDuration: formatDuration(minFirstTripDuration),
        maxFirstTripDuration: formatDuration(maxFirstTripDuration),
        avgLastTripDuration: formatDuration(avgLastTripDuration),
        minLastTripDuration: formatDuration(minLastTripDuration),
        maxLastTripDuration: formatDuration(maxLastTripDuration),
        avgTripDistance: avgTripDistance.toFixed(1),
        minTripDistance: minTripDistance.toFixed(1),
        maxTripDistance: maxTripDistance.toFixed(1),
        avgDailyDistance: avgDailyDistance.toFixed(1),
        totalDistance: trips.reduce((total, trip) => total + trip.distance_traveled, 0).toFixed(1),
        // Ajouter les valeurs brutes pour le tri
        _avgDepartureTime: avgDepartureTime,
        _avgArrivalTime: avgArrivalTime,
        _avgTripDuration: avgTripDuration,
        _avgTripsPerDay: avgTripsPerDay,
        _avgTotalVehicleTime: avgTotalVehicleTime,
        _avgAmplitude: avgAmplitude,
        _avgFirstTripDuration: avgFirstTripDuration,
        _avgLastTripDuration: avgLastTripDuration,
        _avgTripDistance: avgTripDistance
      };
    });
  }, [data]);

  const columns = useMemo(() => [
    {
      key: 'driverName',
      label: 'Conducteur'
    },
    {
      key: 'totalDays',
      label: 'Jours travaillés'
    },
    {
      key: 'totalTrips',
      label: 'Total trajets'
    },
    {
      key: 'avgTripsPerDay',
      label: 'Moyenne trajets/jour',
      sortFunction: (a, b, direction) => {
        const diff = a._avgTripsPerDay - b._avgTripsPerDay;
        return direction === 'ascending' ? diff : -diff;
      }
    },
    {
      key: 'avgDepartureTime',
      label: 'Départ moyen',
      sortFunction: (a, b, direction) => {
        const diff = a._avgDepartureTime - b._avgDepartureTime;
        return direction === 'ascending' ? diff : -diff;
      }
    },
    {
      key: 'avgArrivalTime',
      label: 'Arrivée moyenne',
      sortFunction: (a, b, direction) => {
        const diff = a._avgArrivalTime - b._avgArrivalTime;
        return direction === 'ascending' ? diff : -diff;
      }
    },
    {
      key: 'avgTripDuration',
      label: 'Durée moyenne trajet',
      sortFunction: (a, b, direction) => {
        const diff = a._avgTripDuration - b._avgTripDuration;
        return direction === 'ascending' ? diff : -diff;
      }
    },
    {
      key: 'avgTotalVehicleTime',
      label: 'Temps total véhicule',
      sortFunction: (a, b, direction) => {
        const diff = a._avgTotalVehicleTime - b._avgTotalVehicleTime;
        return direction === 'ascending' ? diff : -diff;
      }
    },
    {
      key: 'avgAmplitude',
      label: 'Amplitude horaire',
      sortFunction: (a, b, direction) => {
        const diff = a._avgAmplitude - b._avgAmplitude;
        return direction === 'ascending' ? diff : -diff;
      }
    },
    {
      key: 'avgFirstTripDuration',
      label: '1er trajet (moy)',
      sortFunction: (a, b, direction) => {
        const diff = a._avgFirstTripDuration - b._avgFirstTripDuration;
        return direction === 'ascending' ? diff : -diff;
      },
      // render: (item) => (
      //   <div className="space-y-1">
      //     <div>{item.avgFirstTripDuration}</div>
      //     <div className="text-xs text-gray-500">
      //       min: {item.minFirstTripDuration}<br/>
      //       max: {item.maxFirstTripDuration}
      //     </div>
      //   </div>
      // )
    },
    {
      key: 'avgLastTripDuration',
      label: 'Dernier trajet (moy)',
      sortFunction: (a, b, direction) => {
        const diff = a._avgLastTripDuration - b._avgLastTripDuration;
        return direction === 'ascending' ? diff : -diff;
      },
      // render: (item) => (
      //   <div className="space-y-1">
      //     <div>{item.avgLastTripDuration}</div>
      //     <div className="text-xs text-gray-500">
      //       min: {item.minLastTripDuration}<br/>
      //       max: {item.maxLastTripDuration}
      //     </div>
      //   </div>
      // )
    },
    {
      key: 'totalDistance',
      label: 'Distance totale (km)',
      sortFunction: (a, b, direction) => {
        const diff = parseFloat(a.totalDistance) - parseFloat(b.totalDistance);
        return direction === 'ascending' ? diff : -diff;
      }
    },
    {
      key: 'avgTripDistance',
      label: 'Distance moy. (km)',
      sortFunction: (a, b, direction) => {
        const diff = a._avgTripDistance - b._avgTripDistance;
        return direction === 'ascending' ? diff : -diff;
      },
      // render: (item) => (
      //   <div className="space-y-1">
      //     <div>{item.avgTripDistance}</div>
      //     <div className="text-xs text-gray-500">
      //       min: {item.minTripDistance}<br/>
      //       max: {item.maxTripDistance}
      //     </div>
      //   </div>
      // )
    }
  ], []);

  if (!driverStats || driverStats.length === 0) {
    return (
      <div className="text-center text-gray-600 dark:text-gray-400">
        Aucune donnée disponible pour le calcul des statistiques par conducteur.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
          Statistiques par conducteur
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Période: {dateRange}
        </p>
      </div>

      {/* Tableau des conducteurs */}
      <SortableTable
        data={driverStats}
        columns={columns}
        emptyMessage="Aucune donnée disponible pour le calcul des statistiques par conducteur."
        countLabel={`Total: ${driverStats.length} conducteur${driverStats.length > 1 ? 's' : ''}`}
        disableFilters={true}
        initialSortBy={{ key: 'driverName', direction: 'ascending' }}
      />
    </div>
  );
}

export default TelematicDrivers;